import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { usePanier } from "../../hooks/usePanier";
import usePlateauxPricing from "../../hooks/usePlateauxPricing";
import { formatCHF } from "../../utils/formatCHF";
import { trackFunnelEventBatch } from "../../utils/trackFunnelBatch";

/* ============================================================
   Image fallback
   ============================================================ */
const DEFAULT_PLATEAU_IMAGE = "/images/plateau-evenement-default.png";

/* ============================================================
   Lecture de la langue active
   ============================================================ */
function getCurrentLang() {
  if (typeof window === "undefined") return "fr";

  const lang = localStorage.getItem("lang");
  return ["fr", "de", "en"].includes(lang) ? lang : "fr";
}

/* ============================================================
   Lecture d’un champ localisé
   ============================================================ */
function getLocalizedValue(value, lang = "fr") {
  if (!value) return "";
  if (typeof value === "string") return value;

  if (typeof value === "object") {
    return value?.[lang] || value?.fr || value?.de || value?.en || "";
  }

  return "";
}

/* ============================================================
   Normalisation de l’image
   ============================================================ */
function getPlateauImage(plateau) {
  const raw = plateau?.imageUrl || plateau?.image_url || plateau?.image || "";

  if (!raw || String(raw).trim() === "") {
    return DEFAULT_PLATEAU_IMAGE;
  }

  const str = String(raw).trim();

  if (str.startsWith("http")) return str;
  if (str.startsWith("/")) return str;
  if (str.startsWith("images/")) return `/${str}`;

  return `/images/${str}`;
}

/* ============================================================
   Hash numérique stable
   ============================================================ */
function hashStringToPositiveInt(input) {
  const str = String(input || "");
  let hash = 0;

  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }

  return Math.abs(hash);
}

/* ============================================================
   Construction d’un id virtuel stable
   ============================================================ */
function buildVirtualPlateauConfigId(signature) {
  const hashed = hashStringToPositiveInt(signature);
  const bounded = hashed % 899999999;
  return 900000 + bounded;
}

/* ============================================================
   Normalisation d’un tableau texte
   ============================================================ */
function normalizeStringArray(values) {
  if (!Array.isArray(values)) return [];

  return values.map((v) => String(v).trim()).filter(Boolean);
}

/* ============================================================
   Tracking funnel sécurisé
   ============================================================ */
function trackPlateauEvent(eventName, metadata = {}) {
  try {
    trackFunnelEventBatch(eventName, metadata);
  } catch (error) {
    // Ne jamais bloquer l’expérience client pour un tracking.
  }
}

/* ============================================================
   Construction du label détaillé
   ============================================================ */
function buildDetailsLabel({ fromages, laitsEviterDisplay }) {
  const parts = [];

  if (fromages && String(fromages).trim() !== "") {
    parts.push(`Préférences : ${String(fromages).trim()}`);
  }

  if (Array.isArray(laitsEviterDisplay) && laitsEviterDisplay.length > 0) {
    parts.push(`À éviter : ${laitsEviterDisplay.join(", ")}`);
  }

  return parts.join(" • ");
}

export default function PlateauConfigurator({
  plateau,
  t = {},
  initialConfig = null,
  onInitialConfigConsumed,
  onGoToShop,
  onGoToCave,
}) {
  const { ajouterAuPanier } = usePanier();

  const [personnes, setPersonnes] = useState(4);
  const [fromages, setFromages] = useState("");
  const [laitsEviter, setLaitsEviter] = useState([]);
  const [justAdded, setJustAdded] = useState(false);
  const [showPostAddActions, setShowPostAddActions] = useState(false);
  const [showReuseChoice, setShowReuseChoice] = useState(false);

  const lastConfigTrackingRef = useRef("");
  const lastViewTrackingRef = useRef("");

  const total = usePlateauxPricing(plateau?.prix, personnes);
  const lang = getCurrentLang();

  const nomPlateau = useMemo(
    () => getLocalizedValue(plateau?.nom, lang),
    [plateau, lang]
  );

  const descriptionPlateau = useMemo(
    () => getLocalizedValue(plateau?.description, lang),
    [plateau, lang]
  );

  const plateauImage = useMemo(() => getPlateauImage(plateau), [plateau]);

  useEffect(() => {
    if (!plateau || !initialConfig) return;

    setPersonnes(Math.max(2, Number(initialConfig?.personnes || 2)));
    setFromages(String(initialConfig?.fromages || "").trim());
    setLaitsEviter(normalizeStringArray(initialConfig?.laitsEviter || []));
    setShowReuseChoice(true);
  }, [plateau, initialConfig]);

  const normalizedPersonnes = useMemo(
    () => Math.max(2, Number(personnes || 2)),
    [personnes]
  );

  const normalizedFromages = useMemo(() => (fromages || "").trim(), [fromages]);

  const normalizedFromagesLower = useMemo(
    () => normalizedFromages.toLowerCase(),
    [normalizedFromages]
  );

  const normalizedLaitsEviter = useMemo(
    () => normalizeStringArray(laitsEviter).sort(),
    [laitsEviter]
  );

  const sourcePlateauId = plateau?.id ?? null;
  const sourcePlateauSlug = plateau?.slug ?? null;
  const sourcePlateauCode = plateau?.code ?? null;

  const plateauType =
    sourcePlateauSlug || sourcePlateauCode || sourcePlateauId || "";

  const trackingMetadata = useMemo(() => {
    return {
      item_type: "plateau_configurator",
      product_scope: "plateau",
      product_id: sourcePlateauId || sourcePlateauSlug || "plateau_configurator",
      product_name: nomPlateau || "Plateau configuré",
      category: "Plateaux événements",
      people_count: normalizedPersonnes,
      plateau_type: plateauType,
      requires_24h: true,
      cheese_preferences: normalizedFromages || "",
      milk_types_to_avoid: normalizedLaitsEviter,
    };
  }, [
    sourcePlateauId,
    sourcePlateauSlug,
    nomPlateau,
    normalizedPersonnes,
    plateauType,
    normalizedFromages,
    normalizedLaitsEviter,
  ]);

  useEffect(() => {
    if (!plateau) return;

    const key = JSON.stringify({
      product_id: trackingMetadata.product_id,
      plateau_type: trackingMetadata.plateau_type,
    });

    if (lastViewTrackingRef.current === key) return;
    lastViewTrackingRef.current = key;

    trackPlateauEvent("view_plateau_configurator", trackingMetadata);
  }, [plateau, trackingMetadata]);

  useEffect(() => {
    if (!plateau) return;

    const key = JSON.stringify({
      product_id: trackingMetadata.product_id,
      people_count: trackingMetadata.people_count,
      cheese_preferences: trackingMetadata.cheese_preferences,
      milk_types_to_avoid: trackingMetadata.milk_types_to_avoid,
    });

    if (lastConfigTrackingRef.current === key) return;

    const timer = window.setTimeout(() => {
      lastConfigTrackingRef.current = key;
      trackPlateauEvent("configure_plateau", trackingMetadata);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [plateau, trackingMetadata]);

  const toggleLait = (type) => {
    setLaitsEviter((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type]
    );
  };

  const signature = useMemo(() => {
    const basePlateauId =
      plateau?.id ?? plateau?.slug ?? plateau?.code ?? "plateau";

    const normalizedLaits = normalizedLaitsEviter.join("-") || "aucun-lait";
    const normalizedPrefs = normalizedFromagesLower || "aucune-preference";

    return [
      String(basePlateauId),
      String(nomPlateau || "plateau-evenement"),
      String(normalizedPersonnes),
      normalizedLaits,
      normalizedPrefs,
    ].join("_");
  }, [
    plateau?.id,
    plateau?.slug,
    plateau?.code,
    nomPlateau,
    normalizedPersonnes,
    normalizedLaitsEviter,
    normalizedFromagesLower,
  ]);

  const virtualPlateauId = useMemo(() => {
    return buildVirtualPlateauConfigId(signature);
  }, [signature]);

  const laitsEviterLabel = useMemo(() => {
    const map = {
      cru: t?.plateauRawMilk || "Lait cru",
      thermise: t?.plateauThermizedMilk || "Lait thermisé",
      pasteurise: t?.plateauPasteurizedMilk || "Lait pasteurisé",
    };

    return normalizedLaitsEviter.map((item) => map[item] || item);
  }, [normalizedLaitsEviter, t]);

  const displayName = useMemo(() => {
    return `${nomPlateau || "Plateau personnalisé"} (${normalizedPersonnes} ${
      t?.plateauPeopleCountLabelShort || "personnes"
    })`;
  }, [nomPlateau, normalizedPersonnes, t]);

  const detailsLabel = useMemo(() => {
    return buildDetailsLabel({
      fromages: normalizedFromages,
      laitsEviterDisplay: laitsEviterLabel,
    });
  }, [normalizedFromages, laitsEviterLabel]);

  const cartKey = useMemo(() => {
    return `platter-${virtualPlateauId}-${signature}`;
  }, [virtualPlateauId, signature]);

  const buildPayloadPanier = () => {
    return {
      id: virtualPlateauId,
      product_id: virtualPlateauId,
      productId: virtualPlateauId,
      produitId: sourcePlateauId,
      sourcePlateauId,
      sourcePlateauSlug,
      sourcePlateauCode,
      cartKey,

      signature,
      plateauSignature: signature,

      name: displayName,
      nom: displayName,
      title: displayName,
      product_name: displayName,
      produitNom: nomPlateau || "Plateau personnalisé",
      details_label: detailsLabel,

      price: total,
      prix: total,
      appliedPrice: total,
      basePrice: total,

      qty: 1,
      quantite: 1,
      quantity: 1,
      pieces: 1,

      image: plateauImage,
      imageUrl: plateauImage,

      item_type: "plateau_configurator",
      type: "plateau_configurator",
      typeProduit: "plateau-evenement",
      product_scope: "plateau",
      source_table: "virtual_platter",
      category: "Plateaux événements",

      isPlateau: true,
      isPlatter: true,
      isConfiguredPlateau: true,
      isConfiguredPlatter: true,
      isVirtualPlatter: true,

      plateau_personnes: normalizedPersonnes,
      plateau_fromages: normalizedFromages,
      plateau_laits_eviter: normalizedLaitsEviter,

      options: {
        personnes: normalizedPersonnes,
        fromages: normalizedFromages,
        laitsEviter: normalizedLaitsEviter,
      },

      plateauConfig: {
        plateau_id: sourcePlateauId,
        plateau_slug: sourcePlateauSlug,
        plateau_code: sourcePlateauCode,
        plateau_nom: nomPlateau || "Plateau personnalisé",
        personnes: normalizedPersonnes,
        fromages: normalizedFromages,
        laitsEviter: normalizedLaitsEviter,
        people_count: normalizedPersonnes,
        plateau_type: plateauType,
        requires_24h: true,
      },

      composition: {
        personnes: normalizedPersonnes,
        fromages: normalizedFromages,
        laitsEviter: normalizedLaitsEviter,
      },
    };
  };

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleGoToShop = useCallback(() => {
    if (typeof onGoToShop === "function") {
      onGoToShop();
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [onGoToShop]);

  const handleGoToCave = useCallback(() => {
    if (typeof onGoToCave === "function") {
      onGoToCave();
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [onGoToCave]);

  const ajouter = () => {
    if (!plateau) return;

    const payloadPanier = buildPayloadPanier();

    trackPlateauEvent("add_plateau_to_cart", {
      ...trackingMetadata,
      product_id: payloadPanier?.product_id || "plateau_configurator",
      product_name: payloadPanier?.product_name || "Plateau configuré",
      cart_total_cents: Math.round(Number(total || 0)),
    });

    ajouterAuPanier({ ...payloadPanier });

    setJustAdded(true);
    setShowPostAddActions(false);
    setShowReuseChoice(false);

    if (typeof onInitialConfigConsumed === "function") {
      onInitialConfigConsumed();
    }

    window.setTimeout(() => {
      setShowPostAddActions(true);
    }, 140);

    window.setTimeout(() => {
      setJustAdded(false);
    }, 1800);
  };

  const handleReuseSameConfig = () => {
    ajouter();
  };

  const handleModifyBeforeAdd = () => {
    setShowReuseChoice(false);
  };

  const decrementPersonnes = () => {
    setPersonnes((prev) => Math.max(2, Number(prev || 2) - 1));
  };

  const incrementPersonnes = () => {
    setPersonnes((prev) => Math.max(2, Number(prev || 2) + 1));
  };

  const handlePersonnesChange = (e) => {
    const next = Number(e.target.value);

    if (!Number.isFinite(next)) {
      setPersonnes(2);
      return;
    }

    setPersonnes(Math.max(2, next));
  };

  if (!plateau) return null;

  return (
    <div className="plateau-configurator-page">
      <style>{`
        .plateau-added-feedback {
          margin-top: 14px;
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid rgba(168, 138, 76, 0.24);
          background: rgba(255, 248, 225, 0.9);
          color: #8b4513;
          font-size: 0.9rem;
          line-height: 1.45;
          text-align: center;
          box-shadow: 0 8px 20px rgba(0,0,0,0.05);
        }

        .plateau-added-feedback strong {
          font-weight: 700;
        }

        .plateau-reorder-box {
          margin-bottom: 18px;
          padding: 16px 18px;
          border-radius: 16px;
          border: 1px solid rgba(191, 167, 106, 0.18);
          background: rgba(255, 250, 240, 0.92);
          box-shadow: 0 10px 24px rgba(0,0,0,0.04);
        }

        .plateau-reorder-title {
          margin: 0 0 6px;
          font-size: 0.95rem;
          font-weight: 700;
          color: #6d552b;
        }

        .plateau-reorder-text {
          margin: 0;
          font-size: 0.92rem;
          line-height: 1.5;
          color: rgba(0,0,0,.72);
        }

        .plateau-reorder-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 12px;
        }
      `}</style>

      <div className="plateau-header">
        <div className="plateau-kicker">
          {t?.plateauConfiguratorKicker || "Configuration du plateau"}
        </div>

        <h1 className="plateau-title">{nomPlateau}</h1>

        <div className="plateau-editorial-highlight">
          {t?.plateauEditorialHighlight ||
            "Plateau artisanal préparé par nos fromagers. Idéal pour vos événements, apéritifs et repas conviviaux."}
        </div>

        {descriptionPlateau ? (
          <p className="plateau-description">{descriptionPlateau}</p>
        ) : null}
      </div>

      {showReuseChoice ? (
        <div className="plateau-reorder-box">
          <div className="plateau-reorder-title">
            {t?.plateauReuseOrderTitle ||
              "Configuration retrouvée depuis votre commande passée"}
          </div>

          <p className="plateau-reorder-text">
            {t?.plateauReuseOrderText ||
              "Vous pouvez recommander ce plateau avec la même configuration, ou la modifier avant ajout au panier."}
          </p>

          <div className="plateau-reorder-actions">
            <button
              type="button"
              className="btn-luxury"
              onClick={handleReuseSameConfig}
            >
              {t?.plateauReuseSameConfig || "REPRENDRE LA MÊME CONFIGURATION"}
            </button>

            <button
              type="button"
              className="btn-luxury-outline"
              onClick={handleModifyBeforeAdd}
            >
              {t?.plateauModifyBeforeAdd || "MODIFIER AVANT AJOUT"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="plateau-consignes-card">
        <div className="plateau-consignes-title">
          {t?.plateauConsignesTitle || "Consignes de plateaux"}
        </div>

        <div className="plateau-consignes-grid">
          <div className="plateau-consigne-item">
            <span className="plateau-consigne-label">
              {t?.plateauConsigneSmall || "Petit"}
            </span>
            <span className="plateau-consigne-price">6.–</span>
          </div>

          <div className="plateau-consigne-item">
            <span className="plateau-consigne-label">
              {t?.plateauConsigneMedium || "Moyen"}
            </span>
            <span className="plateau-consigne-price">8.–</span>
          </div>

          <div className="plateau-consigne-item">
            <span className="plateau-consigne-label">
              {t?.plateauConsigneLarge || "Grand"}
            </span>
            <span className="plateau-consigne-price">9.–</span>
          </div>
        </div>
      </div>

      <div className="plateau-main-layout">
        <div className="plateau-config-card">
          <div className="plateau-section">
            <label className="plateau-label">
              {t?.plateauPeopleLabel || "Nombre de personnes"}
            </label>

            <div className="plateau-personnes-selector">
              <button
                type="button"
                className="plateau-stepper-btn"
                onClick={decrementPersonnes}
                aria-label={
                  t?.plateauDecreasePeople || "Réduire le nombre de personnes"
                }
              >
                −
              </button>

              <input
                type="number"
                min="2"
                value={normalizedPersonnes}
                onChange={handlePersonnesChange}
                className="plateau-personnes-input"
              />

              <button
                type="button"
                className="plateau-stepper-btn"
                onClick={incrementPersonnes}
                aria-label={
                  t?.plateauIncreasePeople || "Augmenter le nombre de personnes"
                }
              >
                +
              </button>
            </div>

            <div className="plateau-helper-text">
              {t?.plateauPeopleHelper ||
                "Le prix est calculé automatiquement selon le nombre de convives."}
            </div>
          </div>

          <div className="plateau-section">
            <label className="plateau-label">
              {t?.plateauCheesePreferencesLabel || "Préférences de fromages"}{" "}
              <span className="plateau-optional">
                {t?.plateauOptional || "(optionnel)"}
              </span>
            </label>

            <textarea
              className="plateau-textarea"
              placeholder={
                t?.plateauCheesePreferencesPlaceholder ||
                "Ex : Comté, Gruyère, Tomme, Bleu..."
              }
              value={fromages}
              onChange={(e) => setFromages(e.target.value)}
            />
          </div>

          <div className="plateau-section">
            <label className="plateau-label">
              {t?.plateauAvoidMilkTypesLabel || "Éviter certains types de lait"}
            </label>

            <div className="plateau-checkbox-grid">
              <label className="plateau-check-card">
                <input
                  type="checkbox"
                  checked={laitsEviter.includes("cru")}
                  onChange={() => toggleLait("cru")}
                />
                <span>{t?.plateauRawMilk || "Lait cru"}</span>
              </label>

              <label className="plateau-check-card">
                <input
                  type="checkbox"
                  checked={laitsEviter.includes("thermise")}
                  onChange={() => toggleLait("thermise")}
                />
                <span>{t?.plateauThermizedMilk || "Lait thermisé"}</span>
              </label>

              <label className="plateau-check-card">
                <input
                  type="checkbox"
                  checked={laitsEviter.includes("pasteurise")}
                  onChange={() => toggleLait("pasteurise")}
                />
                <span>{t?.plateauPasteurizedMilk || "Lait pasteurisé"}</span>
              </label>
            </div>

            <p className="plateau-lait-note">
              {t?.plateauMilkNote ||
                "Les plateaux contiennent plusieurs fromages. Vous pouvez indiquer ici les types de lait que vous souhaitez éviter."}
            </p>
          </div>
        </div>

        <aside className="plateau-summary-card">
          <div className="plateau-summary-block">
            <div className="plateau-summary-label">
              {t?.plateauPricePerPerson || "Prix par personne"}
            </div>
            <div className="plateau-summary-value">
              {formatCHF(plateau.prix)}
            </div>
          </div>

          <div className="plateau-summary-divider" />

          <div className="plateau-summary-block">
            <div className="plateau-summary-label">
              {t?.plateauPeopleCount || "Nombre de personnes"}
            </div>
            <div className="plateau-summary-value">{normalizedPersonnes}</div>
          </div>

          <div className="plateau-summary-divider" />

          <div className="plateau-summary-block">
            <div className="plateau-summary-label">
              {t?.plateauPreferences || "Préférences"}
            </div>
            <div className="plateau-summary-value">
              {normalizedFromages
                ? normalizedFromages
                : t?.plateauNoPreference || "Aucune préférence"}
            </div>
          </div>

          <div className="plateau-summary-divider" />

          <div className="plateau-summary-block">
            <div className="plateau-summary-label">
              {t?.plateauAvoid || "À éviter"}
            </div>
            <div className="plateau-summary-value">
              {laitsEviterLabel.length > 0
                ? laitsEviterLabel.join(", ")
                : t?.plateauNoRestriction || "Aucune restriction"}
            </div>
          </div>

          <div className="plateau-summary-divider" />

          <div className="plateau-total-wrap">
            <div className="plateau-total-label">
              {t?.plateauTotal || "Total"}
            </div>
            <div className="plateau-total-price">{formatCHF(total)}</div>
          </div>

          <button
            className="btn-luxury plateau-add-btn"
            onClick={ajouter}
            type="button"
          >
            {t?.plateauAddToCart || "Ajouter au panier"}
          </button>

          {justAdded && (
            <div className="plateau-added-feedback">
              <strong>✓</strong>{" "}
              {t?.plateauAddedToCart || "Plateau ajouté au panier"}
            </div>
          )}

          <div
            className={`plateau-post-add-actions ${
              showPostAddActions ? "is-visible" : ""
            }`}
            aria-hidden={!showPostAddActions}
          >
            <button
              type="button"
              className="plateau-post-add-btn plateau-post-add-btn-primary"
              onClick={handleGoToShop}
            >
              {t?.plateauPostAddShop || "Découvrir la boutique"}
            </button>

            <button
              type="button"
              className="plateau-post-add-btn plateau-post-add-btn-secondary"
              onClick={handleGoToCave}
            >
              {t?.plateauPostAddCave || "Visiter notre cave"}
            </button>

            <button
              type="button"
              className="plateau-post-add-icon-btn"
              onClick={scrollToTop}
              aria-label={t?.plateauPostAddTop || "Remonter en haut"}
              title={t?.plateauPostAddTop || "Remonter en haut"}
            >
              ↑
            </button>
          </div>

          <div className="plateau-delay-box">
            {t?.plateauDelayInfo ||
              "Les plateaux doivent être commandés au minimum 24h à l'avance."}
          </div>
        </aside>
      </div>
    </div>
  );
}